import { apiSend } from './api.js';
import { auth } from './actor.js';

/**
 * The brokerage write path, as the screens see it.
 *
 * Nothing here validates. `apps/api/src/brokerage.ts` checks the shapes, the
 * database checks the rules, and this layer's job is to carry a refusal back to
 * the screen with its sentence intact — a commission that cannot be invoiced
 * yet comes back saying how much of the deal the buyer has actually paid, and
 * that is the only useful thing to show.
 *
 * Every amount crossing this boundary is a **decimal string**. A quantity of
 * 12.345 MT and a rate of 0.85% both survive `NUMERIC` and neither survives a
 * JSON number.
 */

// ── Reference data ───────────────────────────────────────────────────────────

export interface RefParty {
  code: string;
  name: string;
  isSeller: boolean;
  isBuyer: boolean;
  isSupplier: boolean;
  isCustomer: boolean;
  isSubAgent: boolean;
  countryCode: string | null;
}

export interface RefCode {
  code: string;
  name: string;
}

export interface RefPort extends RefCode {
  countryCode: string | null;
  mode: string;
}

/**
 * Everything the brokerage forms need to offer a choice.
 *
 * `ports` and `incoterms` are here for the availability map, not for a deal:
 * `0021_simplify_deals.sql` took the shipment block off `deals`, so a deal is
 * two parties, some goods and a commission. `salespersons` stays because a
 * commission split still names a beneficiary.
 */
export interface BrokerageRefs {
  parties: RefParty[];
  uoms: Array<RefCode & { decimals: number }>;
  ports: RefPort[];
  salespersons: RefCode[];
  products: RefCode[];
  nextDealNo: string | null;
  enums: {
    kinds: string[];
    currencies: string[];
    incoterms: string[];
    commissionBases: string[];
    accrualPolicies: string[];
    availabilityStatuses: string[];
    paymentMethods: string[];
    transitions: string[];
  };
}

// ── Payloads ─────────────────────────────────────────────────────────────────

export interface DealLinePayload {
  productName: string;
  quantity: string;
  uomCode: string;
  unitRate: string;
  commissionRate?: string | null;
  narration?: string | null;
}

export interface DealSplitPayload {
  subAgentCode?: string | null;
  salespersonCode?: string | null;
  sharePct?: string | null;
  fixedAmountBase?: string | null;
}

export interface DealPayload {
  /** Present when re-saving a draft; absent allocates a number from the series. */
  dealNo?: string | null;
  dealDate: string;
  kind: 'LOCAL' | 'IMPORT';
  partyACode: string;
  partyBCode: string;
  currency: string;
  fxRate: string;
  commissionBasis: string;
  commissionRate?: string | null;
  commissionAmount?: string | null;
  commissionCurrency: string;
  commissionFxRate?: string | null;
  accrualPolicy: string;
  /** From the top bar, not from a control on the form. */
  branchCode?: string | null;
  narration?: string | null;
  lines: DealLinePayload[];
  splits: DealSplitPayload[];
}

export interface SavedDeal {
  dealNo: string;
  status: string;
  dealValueBase: string;
  commissionBase: string;
  lineCount: number;
  message: string;
}

export interface DealTransition {
  dealNo: string;
  status: string;
  message: string;
}

export interface SettlementResult {
  dealNo: string;
  seqNo: number;
  confirmedBase: string;
  balanceBase: string;
  releasedBase: string;
  status: string;
  message: string;
}

export interface CommissionInvoiceResult {
  dealNo: string;
  invoiceNo: string;
  voucherNo: string;
  amountBase: string;
  receivableAccount: string;
  incomeAccount: string;
  status: string;
  message: string;
}

export interface CommissionReceiptResult {
  invoiceNo: string;
  voucherNo: string;
  netBase: string;
  whtBase: string;
  /** Positive is a gain, negative a loss, `'0'` when nothing moved. */
  fxDiffBase: string;
  currency: string;
  estimatedRate: string;
  actualRate: string;
  message: string;
}

/**
 * A row of the availability map, as `GET /api/product-availability` answers it.
 *
 * It lives here rather than on the screen that draws the register because the
 * deal form reads the same list: "who has what" is where a deal starts, so the
 * goods picker offers Party A's own rows before the catalogue.
 */
export interface AvailabilityRow {
  id: string;
  product: string;
  grade: string | null;
  origin: string | null;
  supplier: string;
  supplierCode: string;
  rate: string | null;
  currency: string;
  rateUom: string | null;
  rateUomCode: string | null;
  portOfLoading: string | null;
  portOfLoadingCode: string | null;
  incoterm: string | null;
  leadTimeDays: number | null;
  minQtyBase: string | null;
  maxQtyBase: string | null;
  notes: string | null;
  lastConfirmed: string | null;
  status: string;
}

export interface AvailabilityPayload {
  id?: string | null;
  productName: string;
  supplierCode: string;
  grade?: string | null;
  originCountry?: string | null;
  indicativeRate?: string | null;
  currency?: string;
  rateUomCode?: string | null;
  status?: string;
  portOfLoadingCode?: string | null;
  incoterm?: string | null;
  leadTimeDays?: number | null;
  minQtyBase?: string | null;
  maxQtyBase?: string | null;
  notes?: string | null;
  confirmNow?: boolean;
}

// ── Calls ────────────────────────────────────────────────────────────────────

const deal = (no: string, suffix = '') => `/deals/${encodeURIComponent(no)}${suffix}`;

export function saveDeal(payload: DealPayload): Promise<SavedDeal> {
  return apiSend<SavedDeal>('POST', '/deals', payload, auth());
}

export function deleteDeal(dealNo: string): Promise<DealTransition> {
  return apiSend<DealTransition>('DELETE', deal(dealNo), undefined, auth());
}

/** Move a deal forward. SETTLED, INVOICED and RECEIVED are not in this set —
 *  they are facts about money and are set by the calls that move it. */
export function advanceDeal(
  dealNo: string,
  status: string,
  extra: { date?: string; reason?: string } = {},
): Promise<DealTransition> {
  return apiSend<DealTransition>('POST', deal(dealNo, '/status'), { status, ...extra }, auth());
}

export function recordSettlement(
  dealNo: string,
  payload: {
    settlementDate: string;
    currency: string;
    fxRate: string;
    amount: string;
    paymentMethod?: string | null;
    proofReference?: string | null;
    confirmed: boolean;
    note?: string | null;
  },
): Promise<SettlementResult> {
  return apiSend<SettlementResult>('POST', deal(dealNo, '/settlements'), payload, auth());
}

/** The one call in Part 1 that reaches the general ledger. */
export function raiseCommissionInvoice(
  dealNo: string,
  payload: { invoiceDate?: string; dueDate?: string | null; whtPct?: string; narration?: string },
): Promise<CommissionInvoiceResult> {
  return apiSend<CommissionInvoiceResult>(
    'POST',
    deal(dealNo, '/commission-invoice'),
    payload,
    auth(),
  );
}

/**
 * `fxRate` is the rate the money **actually** converted at, which is not the one
 * the invoice was raised at. Omitted, the invoice's own estimate is used and no
 * exchange difference arises; sent against a base-currency invoice, the server
 * refuses rather than ignores it.
 */
export function recordCommissionReceipt(
  invoiceNo: string,
  payload: {
    receiptDate?: string;
    bankAccountCode?: string | null;
    fxRate?: string;
    narration?: string;
  },
): Promise<CommissionReceiptResult> {
  return apiSend<CommissionReceiptResult>(
    'POST',
    `/commission-invoices/${encodeURIComponent(invoiceNo)}/receipt`,
    payload,
    auth(),
  );
}

export function saveAvailability(
  payload: AvailabilityPayload,
): Promise<{ id: string; message: string }> {
  return apiSend<{ id: string; message: string }>('POST', '/product-availability', payload, auth());
}

export function deleteAvailability(id: string): Promise<{ id: string; message: string }> {
  return apiSend<{ id: string; message: string }>(
    'DELETE',
    `/product-availability/${encodeURIComponent(id)}`,
    undefined,
    auth(),
  );
}
